import { useState } from "react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { contactFormService } from "@project/services/user/contact-form.service";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { useI18n } from "@project/lib/use-i18n";
import { Reveal } from "@project/user/components/motion";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export default function ContactPage() {
  const { t } = useI18n();
  const [f, setF] = useState({
    full_name: "",
    mobile: "",
    mobileCountry: DEFAULT_PHONE_COUNTRY as PhoneCountryCode,
    email: "",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidMobileNumber(f.mobile, f.mobileCountry)) {
      projectToast.error(
        "Enter a valid mobile number for the selected country."
      );
      return;
    }
    setBusy(true);
    try {
      await contactFormService.create({
        full_name: f.full_name.trim(),
        mobile: f.mobile.trim(),
        email: f.email.trim(),
        subject: f.subject.trim(),
        message: f.message.trim(),
      });
      projectToast.success(t("pages.contact.toast.success"));
      setF({
        full_name: "",
        mobile: "",
        mobileCountry: DEFAULT_PHONE_COUNTRY,
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, t("pages.contact.toast.error"))
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <PageShell>
      <PageHero
        eyebrow={t("pages.contact.hero.eyebrow")}
        title={t("pages.contact.hero.title")}
        subtitle={t("pages.contact.hero.subtitle")}
        image={HERO_IMAGES.contact}
      />
      <div className="mx-auto grid max-w-5xl gap-10 px-4 py-12 md:grid-cols-2">
        <div className="space-y-5">
          <div
            className="anim-zoom-in hover-zoom overflow-hidden rounded-2xl border border-border"
            style={{ animationDelay: "150ms" }}
          >
            <img
              src={HERO_IMAGES.contact}
              alt={t("pages.contact.imageAlt")}
              loading="lazy"
              className="aspect-video w-full object-cover"
            />
          </div>
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
            <div
              className="anim-fade-up flex gap-3"
              style={{ animationDelay: "350ms" }}
            >
              <MapPin className="h-5 w-5 shrink-0 text-gold" />
              <div>
                <div className="font-semibold text-primary">
                  {t("pages.contact.info.addressLabel")}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("pages.contact.info.addressValue")}
                </div>
              </div>
            </div>
            <div
              className="anim-fade-up flex gap-3"
              style={{ animationDelay: "470ms" }}
            >
              <Phone className="h-5 w-5 shrink-0 text-gold" />
              <div>
                <div className="font-semibold text-primary">
                  {t("pages.contact.info.phoneLabel")}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("pages.contact.info.phoneValue")}
                </div>
              </div>
            </div>
            <div
              className="anim-fade-up flex gap-3"
              style={{ animationDelay: "590ms" }}
            >
              <Mail className="h-5 w-5 shrink-0 text-gold" />
              <div>
                <div className="font-semibold text-primary">
                  {t("pages.contact.info.emailLabel")}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("pages.contact.info.emailValue")}
                </div>
              </div>
            </div>
            <div
              className="anim-fade-up flex gap-3"
              style={{ animationDelay: "710ms" }}
            >
              <Clock className="h-5 w-5 shrink-0 text-gold" />
              <div>
                <div className="font-semibold text-primary">
                  {t("pages.contact.info.hoursLabel")}
                </div>
                <div className="text-sm text-muted-foreground">
                  <div>{t("pages.contact.info.hoursValueMorning")}</div>
                  <div>{t("pages.contact.info.hoursValueEvening")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Reveal variant="right">
          <form
            onSubmit={submit}
            className="anim-fade-up h-full space-y-3 rounded-2xl border border-border bg-card p-6 shadow-sm"
            style={{ animationDelay: "250ms" }}
          >
            <h2 className="font-serif text-xl font-bold text-primary">
              {t("pages.contact.form.heading")}
            </h2>
            <div>
              <Label>{t("pages.contact.form.fullName")}</Label>
              <Input
                required
                value={f.full_name}
                onChange={e => setF({ ...f, full_name: e.target.value })}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>{t("pages.contact.form.mobile")}</Label>
                <MobileNumberField
                  value={f.mobile}
                  country={f.mobileCountry}
                  onCountryChange={code => setF({ ...f, mobileCountry: code })}
                  onChange={mobile => setF({ ...f, mobile })}
                />
              </div>
              <div>
                <Label>{t("pages.contact.form.email")}</Label>
                <Input
                  type="email"
                  value={f.email}
                  onChange={e => setF({ ...f, email: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>{t("pages.contact.form.subject")}</Label>
              <Input
                value={f.subject}
                onChange={e => setF({ ...f, subject: e.target.value })}
              />
            </div>
            <div>
              <Label>{t("pages.contact.form.message")}</Label>
              <Textarea
                required
                rows={5}
                value={f.message}
                onChange={e => setF({ ...f, message: e.target.value })}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="hover:bg-gold/90 btn-lift w-full bg-gold text-gold-foreground hover:shadow-md"
            >
              {busy
                ? t("pages.contact.form.sending")
                : t("pages.contact.form.submit")}
            </Button>
          </form>
        </Reveal>
      </div>
    </PageShell>
  );
}
